import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { Pagination } from '@/dtos';
import type { ReviewList } from '@/dtos/Review';
import axiosInstance from '@/plugins/axios';
import { createAppAsyncThunk } from '@/store/thunk';

interface HowToReviewsState {
  loading: boolean;
  list: ReviewList[];
  item: ReviewList | null;
  page: number;
  pageSize: number;
  total: number;
}

const initialState: HowToReviewsState = {
  loading: false,
  list: [],
  item: null,
  page: 0,
  pageSize: 8,
  total: 0
};

export const requestHowToReviews = createAppAsyncThunk(
  'how-to-reviews/getList',
  async (data: { page: number; pageSize: number; title?: string }) => {
    try {
      const response = await axiosInstance.get<undefined, Pagination<ReviewList>>('/public/how-to-reviews', {
        params: {
          page: data.page,
          pageSize: data.pageSize,
          ...(data.title ? { title: data.title } : {})
        }
      });
      return response;
    } catch {
      return;
    }
  }
);

export const requestHowToReview = createAppAsyncThunk('how-to-reviews/getItem', async (id: string) => {
  try {
    const response = await axiosInstance.get<undefined, ReviewList>(`/public/reviews/${id}`);
    return response;
  } catch {
    return;
  }
});

export const howToReviewsSlice = createSlice({
  name: 'how-to-reviews',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    resetReviews: (state) => {
      state.loading = false;
      state.list = [];
      state.item = null;
      state.page = 0;
      state.pageSize = 8;
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestHowToReviews.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestHowToReviews.rejected, (state) => {
        // state.list = [];
        state.item = null;
        state.page = 0;
        state.pageSize = 8;
        state.total = 0;
        state.loading = false;
      })
      .addCase(requestHowToReviews.fulfilled, (state, action) => {
        if (action.payload) {
          const isNewSearch = action.payload.page === 0;

          state.list = isNewSearch
            ? action.payload.items
            : [...state.list, ...action.payload.items.filter((i) => !state.list.find((l) => l.id === i.id))];

          state.total = action.payload.total;
          state.page = action.payload.page;
          state.pageSize = action.payload.pageSize;
        }
        state.loading = false;
      });
    builder
      .addCase(requestHowToReview.pending, (state) => {
        state.item = null;
        state.loading = true;
      })
      .addCase(requestHowToReview.rejected, (state) => {
        state.loading = false;
      })
      .addCase(requestHowToReview.fulfilled, (state, action) => {
        if (action.payload) {
          state.item = action.payload;
        }
        state.loading = false;
      });
  }
});

export const { setPage, setPageSize } = howToReviewsSlice.actions;
